// The AI interviewer's presence: Maya's face, inside an aura that carries her
// state.
//
// WHY A DRAWN CHARACTER AND NOT A PHOTOREAL ONE
// A realistic face implies a person is watching, which is untrue, and it works
// against the disclosure Maya makes out loud in her own greeting. It also sits
// in the uncanny valley — and this product scores hesitation, so unsettling the
// candidate corrupts the thing being measured. A flat illustration says "a
// system with a personality" and claims nothing more. See InterviewerAvatar.
//
// WHERE THE CHARACTER ACTUALLY COMES FROM
// Behaviour, not features. Four states, each visibly different at a glance:
//
//   speaking   Maya is asking — faster pulse, mouth moves, aura brightens
//   listening  the candidate is recording — the aura tracks their real mic level
//   thinking   their answer is uploading — a slow, deliberate pause
//   asking     waiting for them to begin — a slow idle breath
//
// WHY THE AURA REACTS TO THE MICROPHONE
// This is the part that earns its place. While recording it responds to the
// candidate's actual voice, which makes it a live "we can hear you" indicator
// for the whole interview rather than only at the device check. The single worst
// outcome in this product is somebody answering every question into a dead
// microphone; a visual that moves when they speak makes that obvious in seconds
// instead of at the end.
//
// WHY THE AURA AND NOT THE FACE REACTS
// A face that changes with your volume is unnerving. The aura reads as her
// attention; the face stays composed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import InterviewerAvatar from "./InterviewerAvatar";

// Maya's palette. Warm on purpose, and deliberately distinct from the product's
// teal so she is identifiable as herself rather than as more interface. Amber
// reads warm without reading like a warning, which red and orange both do.
const MAYA = {
  glow: "251, 191, 116",
  ring: "#f6c28b",
  ringSoft: "rgba(246,194,139,.42)",
  text: "#f8cfa3",
  deep: "#2a1f18",
  deeper: "#1a1310",
  hair: "#2e2320",
  hairLight: "#4a3a33",
  skin: "#e8bb96",
  skinShade: "#d3a37c",
  line: "#3a2b24",
  blush: "#e08b6f",
  clothes: "#3d4f5c",
};

const LABELS = {
  speaking: "Asking you a question",
  listening: "Listening…",
  thinking: "One moment…",
  asking: "Ready when you are",
  idle: "Ready when you are",
};

const InterviewerOrb = ({ state = "idle", stream = null, label, showAvatar = true }) => {
  // state: "idle" | "asking" | "listening" | "speaking" | "thinking"
  const [level, setLevel] = useState(0);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    // Only analyse while listening. Holding an AudioContext open for the whole
    // session drains battery on a phone for no benefit.
    if (state !== "listening" || !stream) {
      setLevel(0);
      return undefined;
    }

    let cancelled = false;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      ctxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        if (cancelled) return;
        analyser.getByteTimeDomainData(data);
        let max = 0;
        for (let i = 0; i < data.length; i++) {
          max = Math.max(max, Math.abs(data[i] - 128));
        }
        // Smoothed, so the ring breathes rather than jitters.
        setLevel((prev) => prev * 0.7 + Math.min(1, (max / 128) * 2.2) * 0.3);
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch {
      // No analyser available — the aura still animates on its idle rhythm.
    }

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ctxRef.current?.state !== "closed") ctxRef.current?.close().catch(() => {});
    };
  }, [state, stream]);

  const listening = state === "listening";
  const speaking = state === "speaking";
  const thinking = state === "thinking";
  const asking = state === "asking" || state === "idle";

  // Scale follows the voice while listening; otherwise a slow synthetic breath,
  // so the panel is never completely static — a frozen presence reads as a
  // crashed page.
  const scale = listening ? 1 + level * 0.3 : 1;
  const glow = listening
    ? 0.35 + level * 0.5
    : speaking
      ? 0.72
      : thinking
        ? 0.3
        : 0.24;

  // A brief settle when the state changes, so transitions have a beat rather
  // than snapping. Keyed on state so it replays each time.
  const settleKey = useMemo(() => state, [state]);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 200,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        background:
          "radial-gradient(120% 120% at 50% 0%, #2a2018 0%, #14100e 55%, #070d14 100%)",
        overflow: "hidden",
        transition: "background .6s ease",
      }}
    >
      {/* Faint grid, for depth rather than decoration. */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.1,
          backgroundImage: `linear-gradient(rgba(${MAYA.glow},.3) 1px, transparent 1px), linear-gradient(90deg, rgba(${MAYA.glow},.3) 1px, transparent 1px)`,
          backgroundSize: "34px 34px",
          maskImage: "radial-gradient(circle at 50% 45%, #000 20%, transparent 72%)",
          WebkitMaskImage:
            "radial-gradient(circle at 50% 45%, #000 20%, transparent 72%)",
        }}
      />

      <Box
        key={settleKey}
        sx={{
          position: "relative",
          width: 152,
          height: 152,
          display: "grid",
          placeItems: "center",
          animation: "mayaSettle .45s cubic-bezier(.22,1,.36,1)",
          "@keyframes mayaSettle": {
            from: { transform: "scale(.965)" },
            to: { transform: "scale(1)" },
          },
        }}
      >
        {/* Outer reactive ring — her attention */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `1px solid rgba(${MAYA.glow},.4)`,
            transform: `scale(${scale})`,
            transition: listening ? "transform .06s linear" : "transform .5s ease",
            boxShadow: `0 0 ${30 + level * 46}px rgba(${MAYA.glow},${glow})`,
          }}
        />
        {/* Middle ring, counter-rotating so she reads as present rather than
            paused. Slower while thinking: a fast spin during a wait looks like
            a loading state that has hung. */}
        <Box
          sx={{
            position: "absolute",
            inset: 8,
            borderRadius: "50%",
            border: `1px dashed rgba(${MAYA.glow},.3)`,
            animation: `orbSpin ${thinking ? 26 : 16}s linear infinite`,
            "@keyframes orbSpin": { to: { transform: "rotate(360deg)" } },
          }}
        />

        {showAvatar ? (
          <Box
            sx={{
              position: "relative",
              animation: speaking
                ? "orbBreathe 1.2s ease-in-out infinite"
                : asking
                  ? "orbBreathe 3.2s ease-in-out infinite"
                  : "none",
              "@keyframes orbBreathe": {
                "0%,100%": { opacity: 0.94 },
                "50%": { opacity: 1 },
              },
            }}
          >
            <InterviewerAvatar size={118} palette={MAYA} state={state} />
          </Box>
        ) : (
          // The pure orb, kept behind a prop so the two can be compared without
          // a rebuild.
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: `radial-gradient(circle at 35% 30%, ${MAYA.ring} 0%, #d99354 45%, #8a5a2c 100%)`,
              boxShadow: `inset 0 0 24px rgba(0,0,0,.45), 0 0 ${18 + level * 30}px rgba(${MAYA.glow},.5)`,
              transform: `scale(${1 + level * 0.12})`,
              transition: listening ? "transform .06s linear" : "transform .5s ease",
            }}
          />
        )}
      </Box>

      <Box sx={{ position: "relative", textAlign: "center", px: 2 }}>
        {/* Her name, always. A presence with no name is a widget; the same
            presence with a name is someone the candidate is talking to. */}
        <Typography
          sx={{
            color: MAYA.text,
            fontSize: 13.5,
            fontWeight: 750,
            letterSpacing: 0.2,
          }}
        >
          {label || "Maya"}
        </Typography>
        <Typography
          sx={{
            color: "rgba(226,232,240,.6)",
            fontSize: 12,
            mt: 0.3,
            minHeight: 18,
            transition: "opacity .3s ease",
          }}
        >
          {LABELS[state] || LABELS.idle}
        </Typography>
      </Box>
    </Box>
  );
};

export default InterviewerOrb;
